import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MessageCircle, ArrowLeft, MapPin, Scissors, Shirt, Clock, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSEO from "@/hooks/useSEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE_NUMBER = "+919068155122";

const CITIES_SERVED = "Delhi NCR, Mumbai, Bangalore, Hyderabad, Chennai, Kolkata, Jaipur, Lucknow, Ahmedabad, Pune, Surat, Chandigarh, Dubai, Abu Dhabi, Riyadh, London, New York, Toronto, Sydney, Singapore, Paris";

const FABRIC_INFO = {
  "aari": { fabrics: "Silk, Georgette, Chiffon, Net, Velvet, Cotton, Organza, Crepe", process: "The fabric is stretched on a wooden frame (adda). The artisan uses a hooked aari needle to pull thread from underneath, creating continuous chain stitches at remarkable speed. Designs are first traced onto the fabric, then filled with intricate stitching. A skilled artisan can embroider complex patterns in hours rather than days." },
  "zardozi": { fabrics: "Velvet, Silk, Satin, Brocade, Raw Silk, Heavy Georgette", process: "The artisan stretches fabric on a frame and uses a needle threaded with gold or silver metallic wire (called zari). Patterns are first sketched on the fabric. The artisan then stitches metallic threads, beads, and sequins following the design. Each element is secured individually with tiny stitches, creating a raised, dimensional finish." },
  "dabka": { fabrics: "Velvet, Silk, Satin, Net, Georgette, Crepe", process: "Dabka uses tightly coiled metallic wire (resembling a spring). The artisan cuts the coil to required lengths and stitches each piece flat or raised onto the fabric. The coiled wire creates a distinctive textured, three-dimensional effect. Often combined with Zardozi and Mukaish for multi-layered designs." },
  "mirror-work": { fabrics: "Cotton, Silk, Georgette, Chanderi, Bandhani, Kota Doria", process: "Small mirrors (shisha) are placed on the fabric and secured using decorative interlocking stitches around each mirror. The surrounding threadwork holds the mirror firmly while creating an ornamental frame. Traditional Kutch and Rajasthani patterns use geometric arrangements of mirrors." },
  "tilla": { fabrics: "Pashmina, Silk, Wool, Tweed, Cotton Silk", process: "Gold or silver metallic threads are couched (laid on the surface and stitched down) to create elaborate geometric and floral patterns. The threads are not pulled through the fabric but secured on top, creating a distinctive raised metallic surface. Kashmiri artisans follow centuries-old pattern templates." },
  "mukaish": { fabrics: "Chiffon, Georgette, Muslin, Cotton, Net, Silk", process: "Tiny pieces of metallic wire (badla) are flattened and inserted through the fabric using a specialized needle. Each piece creates a small dot of shimmer. Thousands of these dots create patterns that give a subtle, star-like sparkle effect. Often paired with Chikankari embroidery from Lucknow." },
  "pearl-work": { fabrics: "Silk, Satin, Net, Organza, Velvet, Tulle", process: "Freshwater or cultured pearls are individually hand-sewn onto fabric using fine silk thread. Each pearl is secured with multiple stitches to prevent movement. Artisans create floral motifs, borders, scattered patterns, and full-coverage designs. The weight and drape of the fabric changes with heavy pearl application." },
  "crystal-work": { fabrics: "Silk, Net, Organza, Tulle, Satin, Georgette, Velvet", process: "Swarovski and premium crystals are individually heat-set or hand-stitched onto fabric. Each crystal is positioned precisely to create patterns that catch and reflect light from every angle. Artisans combine different crystal sizes and cuts for gradient and 3D effects." },
  "cutdana": { fabrics: "Silk, Georgette, Velvet, Net, Satin, Organza", process: "Faceted glass or crystal beads (cutdana) are hand-stitched individually onto fabric. Each bead has multiple cut surfaces that reflect light, creating sophisticated sparkle. Artisans arrange cutdana in lines, clusters, or scattered patterns to create depth and dimension." },
  "beadwork": { fabrics: "Silk, Net, Georgette, Cotton, Velvet, Satin, Denim", process: "Tiny glass, crystal, or specialty beads are hand-stitched onto fabric using fine needles and thread. Beads can be applied individually, in rows, or in clustered patterns. The technique allows for incredible detail and texture variation." },
  "sequin-work": { fabrics: "Georgette, Net, Silk, Satin, Velvet, Organza, Tulle", process: "Sequins are individually hand-applied using needle and thread, with each sequin secured by stitches through its center hole. Unlike machine-applied sequins, hand-sewn sequins are flat, secure, and uniform. Artisans create patterns, borders, and full-coverage shimmer effects." },
  "gota-work": { fabrics: "Cotton, Silk, Chanderi, Kota Doria, Georgette, Net", process: "Gold or silver zari ribbon (gota) is cut into shapes — petals, leaves, circles — and appliqued onto fabric. Each piece is folded, shaped, and stitched by hand. The layered ribbon creates a beautiful raised, metallic effect. Traditional Rajasthani technique passed through generations." },
  "jaal-work": { fabrics: "Net, Organza, Georgette, Silk, Chiffon, Tulle", process: "A continuous, interconnected embroidery pattern covers the entire fabric surface. The artisan works section by section, creating a mesh-like repeating design. Jaal patterns can incorporate any stitch — chain, stem, satin — creating an all-over effect that is labor-intensive but visually stunning." },
  "jadau": { fabrics: "Velvet, Silk, Satin, Heavy Brocade, Tissue", process: "Precious and semi-precious stones are set into a metallic base that is stitched onto fabric. The setting technique (borrowed from jewelry making) uses gold or silver wire to create bezels for each stone. This creates a jewel-encrusted effect on fabric, often used for royal and bridal wear." },
  "leather-embroidery": { fabrics: "Genuine Leather, Faux Leather, Suede, Nubuck, Rexine", process: "Specialized needles and techniques are used to embroider directly onto leather surfaces. Pre-punched holes guide the needle to prevent tearing. Artisans combine traditional embroidery stitches with leather's unique texture to create contemporary designs for fashion and accessories." },
  "applique": { fabrics: "Cotton, Silk, Velvet, Felt, Net, Organza, Denim", process: "Pieces of fabric are cut into shapes — flowers, animals, geometric forms — and layered onto a base fabric. Each piece is secured with decorative stitching (blanket stitch, satin stitch, or running stitch). The layering creates bold, colorful, dimensional designs." },
  "resham-work": { fabrics: "Silk, Cotton, Georgette, Chiffon, Chanderi, Linen, Muslin", process: "Colored silk threads (resham) are used to create flowing embroidery patterns using satin stitch, stem stitch, long-and-short stitch, and French knots. The silk thread's natural sheen creates vibrant, lustrous designs. Artisans blend multiple thread colors for realistic floral and nature-inspired motifs." }
};

export default function ServiceDetailPage() {
  const { serviceId } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allServices, setAllServices] = useState([]);

  const extra = FABRIC_INFO[serviceId] || { fabrics: "Various premium fabrics", process: "Handcrafted by master artisans using traditional techniques passed through generations." };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sRes, allRes] = await Promise.all([
          axios.get(`${API}/services/${serviceId}`),
          axios.get(`${API}/services`)
        ]);
        setService(sRes.data);
        setAllServices(allRes.data.filter(s => s.id !== serviceId).slice(0, 4));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [serviceId]);

  const seoName = service?.name || serviceId;
  const lehengaKeyword = seoName ? `${seoName.toLowerCase()} lehenga` : '';
  useSEO({
    title: `${seoName} Embroidery Services India | ${seoName} Lehenga & Bridal Work | KarigariHub`,
    description: `Custom ${seoName} embroidery for bridal lehengas, lehenga choli, sarees & couture. ${seoName} lehenga embroidery by master karigars. Delhi, Mumbai, Dubai, London, New York. WhatsApp +91 9068155122.`,
    keywords: `${seoName} embroidery, ${lehengaKeyword}, ${seoName} embroidery near me, ${seoName} for bridal lehenga, ${seoName} lehenga designs, custom ${seoName} India, ${seoName} embroidery cost, ${seoName} lehenga choli, ${seoName} embroidery Delhi Mumbai Dubai London, ${seoName} work online order, ${seoName} artisan India, ${seoName} embroidery price per sq ft`,
    aiDescription: `KarigariHub ${seoName} embroidery service — expert hand embroidery for lehengas, bridal lehengas, lehenga choli, blouses, dupattas, and couture garments. ${service?.description || ''} Origin: ${service?.origin || 'India'}. Techniques: ${service?.techniques?.join(', ') || ''}. Best for: ${service?.best_for?.join(', ') || ''}. ${seoName} lehenga embroidery available for custom orders. Pricing subject to karigar availability and seasonal demand. Available in ${CITIES_SERVED}. Contact: +91 9068155122.`,
    path: `/services/${serviceId}`,
    jsonLd: service ? {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": `${service.name} Embroidery Service`,
      "provider": {
        "@type": "Organization",
        "name": "KarigariHub",
        "telephone": "+919068155122",
        "url": "https://karigarihubindia.com",
        "address": { "@type": "PostalAddress", "addressCountry": "IN", "addressRegion": "Haryana" }
      },
      "description": `Custom ${service.name} embroidery for lehengas, bridal wear, and couture garments. ${service.description}`,
      "areaServed": [
        {"@type":"Country","name":"India"},{"@type":"Country","name":"United Arab Emirates"},
        {"@type":"Country","name":"United States"},{"@type":"Country","name":"United Kingdom"},
        {"@type":"Country","name":"Canada"},{"@type":"Country","name":"Australia"},
        {"@type":"Country","name":"Singapore"},{"@type":"Country","name":"France"},
        {"@type":"Country","name":"Germany"},{"@type":"Country","name":"Saudi Arabia"}
      ],
      "serviceType": `${service.name} Hand Embroidery`,
      "image": service.images?.[0] ? `https://karigarihubindia.com${service.images[0]}` : undefined,
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": `${service.name} Embroidery Services`,
        "itemListElement": [
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": `${service.name} Lehenga Embroidery`}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": `${service.name} Bridal Embroidery`}},
          {"@type": "Offer", "itemOffered": {"@type": "Service", "name": `${service.name} Blouse Embroidery`}}
        ]
      }
    } : undefined
  });

  const handleWhatsApp = () => {
    const msg = `Hello! I'm interested in ${seoName} embroidery services. Can you share pricing and availability?`;
    window.open(`https://wa.me/${PHONE_NUMBER.replace('+', '')}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  if (loading) return (
    <div className="min-h-screen"><Navbar /><div className="pt-32 flex justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div></div>
  );

  if (!service) return (
    <div className="min-h-screen"><Navbar /><div className="pt-32 container-main text-center"><h1 className="text-2xl font-semibold mb-4">Service not found</h1><Link to="/services"><Button>View All Services</Button></Link></div><Footer /></div>
  );

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Breadcrumb + Header */}
      <section className="pt-28 pb-12 bg-white" data-testid="service-detail-header">
        <div className="container-main">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-foreground">Services</Link>
            <span>/</span>
            <span className="text-foreground">{service.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Image Gallery */}
            <div>
              <img
                src={service.images[0]}
                alt={`${service.name} hand embroidery service India - custom bridal and designer work by KarigariHub artisan`}
                className="w-full h-80 object-cover rounded-lg mb-3"
              />
              <div className="grid grid-cols-4 gap-2">
                {service.images.slice(1, 5).map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`${service.name} embroidery sample ${i + 2} - handcrafted Indian artisan work`}
                    className="w-full h-20 object-cover rounded"
                    loading="lazy"
                  />
                ))}
              </div>
            </div>

            {/* Info */}
            <div>
              <span className="text-xs uppercase tracking-wider text-[#D4AF37] font-medium">{service.category} embroidery</span>
              <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 mb-3">
                {service.name} Embroidery Services
              </h1>
              <p className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                <MapPin className="w-4 h-4" /> Origin: {service.origin}
              </p>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">{service.description}</p>

              <div className="flex flex-wrap gap-2 mb-6">
                <Button className="bg-[#25D366] hover:bg-[#25D366]/90" onClick={handleWhatsApp} data-testid="detail-whatsapp">
                  <MessageCircle className="w-4 h-4 mr-2" />Get a Free Quote
                </Button>
                <Link to="/contact">
                  <Button variant="outline" data-testid="detail-contact">Send Inquiry</Button>
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-start gap-2">
                  <Star className="w-4 h-4 text-[#D4AF37] mt-0.5" />
                  <div><p className="font-medium">Techniques</p><p className="text-muted-foreground">{service.techniques?.join(", ")}</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <Shirt className="w-4 h-4 text-[#D4AF37] mt-0.5" />
                  <div><p className="font-medium">Best For</p><p className="text-muted-foreground">{service.best_for?.join(", ")}</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <Scissors className="w-4 h-4 text-[#D4AF37] mt-0.5" />
                  <div><p className="font-medium">Fabrics</p><p className="text-muted-foreground">{extra.fabrics}</p></div>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-[#D4AF37] mt-0.5" />
                  <div><p className="font-medium">Turnaround</p><p className="text-muted-foreground">7-21 days (varies by complexity)</p></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-12 bg-[#F9F9F7]" data-testid="service-process">
        <div className="container-main">
          <h2 className="font-heading text-2xl font-semibold mb-4">How {service.name} Embroidery is Done</h2>
          <p className="text-muted-foreground text-sm leading-relaxed max-w-3xl mb-6">{extra.process}</p>

          <h2 className="font-heading text-2xl font-semibold mb-4 mt-10">History & Heritage</h2>
          <p className="text-muted-foreground text-sm leading-relaxed max-w-3xl">{service.history}</p>
        </div>
      </section>

      {/* Cities */}
      <section className="py-12 bg-white" data-testid="service-cities">
        <div className="container-main">
          <h2 className="font-heading text-2xl font-semibold mb-4">
            {service.name} Embroidery Services — Cities We Deliver To
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed mb-6">
            Custom {service.name} embroidery is available for delivery to: <strong>{CITIES_SERVED}</strong>, and all other cities worldwide with tracked international shipping.
            Whether you need {service.name} for a bridal lehenga in Delhi, a designer saree in Dubai, or a couture piece in London — KarigariHub's master artisans deliver to your doorstep.
            Our work is on par with leading specialists like <strong>Fareenas in Delhi</strong>, <strong>Aman Embroidery in Mumbai</strong>, and <strong>Mongas in London</strong> — with the added benefit of direct artisan pricing and a fully customizable design process.
          </p>
          <div className="flex flex-wrap gap-2">
            {CITIES_SERVED.split(", ").map(city => (
              <span key={city} className="px-3 py-1.5 rounded-full border border-border text-xs text-muted-foreground bg-[#F9F9F7]">
                {service.name} in {city}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-12 bg-[#F9F9F7]" data-testid="related-services">
        <div className="container-main">
          <h2 className="font-heading text-2xl font-semibold mb-6">Other Embroidery Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {allServices.map(s => (
              <Link key={s.id} to={`/services/${s.id}`} className="group">
                <div className="border border-border rounded-lg overflow-hidden bg-white hover:border-[#D4AF37]/40 transition-colors">
                  <img src={s.images[0]} alt={`${s.name} embroidery services India`} className="w-full h-32 object-cover" loading="lazy" />
                  <div className="p-3">
                    <h3 className="font-heading text-sm font-semibold group-hover:text-[#D4AF37] transition-colors">{s.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{s.origin}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/services"><Button variant="outline" data-testid="view-all-services">View All 17 Services</Button></Link>
          </div>
        </div>
      </section>

      {/* GEO Expert Section */}
      <section className="py-12 bg-white" data-testid="service-expert">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-semibold mb-2">{service.name} for Lehengas — What You Need to Know</h2>
              <p className="text-muted-foreground text-sm">Expert insights from our master karigars</p>
            </div>
            <div className="prose prose-sm max-w-none text-muted-foreground leading-relaxed space-y-4">
              <p>{service.name} is one of the most sought-after embroidery techniques for bridal lehengas and designer wear in India. When applied to lehenga fabric, {service.name.toLowerCase()} creates {service.name === "Zardozi" ? "a regal, raised metallic surface that catches light beautifully — the signature of Mughal-inspired bridal lehengas" : service.name === "Aari" ? "intricate chain-stitch patterns that flow seamlessly across the fabric — ideal for both modern and traditional lehenga designs" : service.name === "Mirror Work" ? "a dazzling, light-catching surface that is quintessentially Rajasthani — perfect for navratri chaniya choli and festive lehengas" : service.name === "Gota Patti" ? "beautiful raised metallic ribbon patterns — the hallmark of traditional Rajasthani bridal lehengas" : `stunning decorative effects that elevate any lehenga design — from simple festive wear to heavy bridal pieces`}.</p>
              <p>At KarigariHub, {service.name.toLowerCase()} embroidery for lehengas is performed by dedicated master artisans with decades of experience. Whether you need {service.name.toLowerCase()} on a red bridal lehenga, a pastel engagement lehenga, or a designer lehenga choli for a boutique collection — our karigars deliver consistent, designer-level quality.</p>
              <p className="text-xs italic">Note: All pricing for {service.name.toLowerCase()} embroidery is indicative and subject to karigar availability, seasonal demand, and design complexity. During peak wedding season (Oct-Feb), lead times may be longer. <Link to="/estimate" className="text-[#D4AF37] hover:underline">Use our cost estimator</Link> for an instant indicative range, or <a href={`https://wa.me/${PHONE_NUMBER.replace('+', '')}?text=Hi%2C%20I%20need%20${encodeURIComponent(service.name)}%20embroidery%20for%20my%20lehenga`} className="text-[#D4AF37] hover:underline">WhatsApp us</a> for an exact quote.</p>
            </div>

            {/* Internal links for SEO */}
            <div className="mt-8 p-5 bg-[#F9F9F7] border border-border rounded-xl">
              <h3 className="font-heading font-semibold text-sm mb-3">Explore Related Pages</h3>
              <div className="flex flex-wrap gap-2">
                <Link to="/lehenga-inspiration" className="text-xs text-[#D4AF37] hover:underline">Lehenga Inspiration Gallery</Link>
                <span className="text-border">|</span>
                <Link to="/custom-bridal-lehengas" className="text-xs text-[#D4AF37] hover:underline">Bridal Lehenga Embroidery</Link>
                <span className="text-border">|</span>
                <Link to="/pricing-guide" className="text-xs text-[#D4AF37] hover:underline">Full Pricing Guide</Link>
                <span className="text-border">|</span>
                <Link to="/estimate" className="text-xs text-[#D4AF37] hover:underline">Cost Estimator</Link>
                <span className="text-border">|</span>
                <Link to="/get-your-lehenga" className="text-xs text-[#D4AF37] hover:underline">Get Your Lehenga Made</Link>
                <span className="text-border">|</span>
                <Link to="/international-orders" className="text-xs text-[#D4AF37] hover:underline">International Orders</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-[#1A1A1A] text-white" data-testid="service-cta">
        <div className="container-main text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-3">
            Ready to Order Custom {service.name}?
          </h2>
          <p className="text-white/70 text-sm max-w-md mx-auto mb-6">
            Share your design on WhatsApp and get a free quote within 24 hours. We deliver worldwide.
          </p>
          <Button className="bg-[#25D366] hover:bg-[#25D366]/90" onClick={handleWhatsApp} data-testid="cta-whatsapp">
            <MessageCircle className="w-4 h-4 mr-2" />WhatsApp +91 9068155122
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
